/**
 * VoiceTabContent — Voice tab content for the UnifiedBottomPanel.
 *
 * CharacterDesigner only. Shows the character-owned voice cast source of truth:
 * - Current picked voice profile from out_voice_profile
 * - Voice variants from the character sheet voice history
 * - "Regenerate Voice" to append a new voice variant
 */

import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pause, Play, RefreshCw } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import { VoiceCatalogPicker, type VoiceCatalogPickerProps } from './VoiceCatalogPicker'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VoiceVariant {
  index: number
  voiceName?: string
  voiceId?: string
  provider?: string
  audioSample?: string
  audioUrl?: string | null
  speechText?: string
  createdAt?: string
}

export interface VoiceTabContentProps {
  voiceName?: string
  voiceSampleUrl?: string | null
  speechText?: string
  variants?: VoiceVariant[]
  selectedVariantIndex?: number
  onSelectVariant?: (index: number) => void
  onRegenerateVoice?: () => void
  isRunning?: boolean
  hasCharacterSheet?: boolean
  voiceCatalog?: VoiceCatalogPickerProps
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Static waveform bar heights (10 bars) — matches CharacterDesignerNode Quick Info Bar */
const WAVEFORM_BAR_HEIGHTS = [3, 5, 7, 4, 6, 8, 5, 3, 6, 4]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const VoiceTabContent = memo(function VoiceTabContent({
  voiceName,
  voiceSampleUrl,
  speechText,
  variants = [],
  selectedVariantIndex,
  onSelectVariant,
  onRegenerateVoice,
  isRunning,
  hasCharacterSheet,
  voiceCatalog,
}: VoiceTabContentProps) {
  const { t } = useTranslation('canvas')
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)
  const [playingKey, setPlayingKey] = useState<string | null>(null)

  const hasVoice = !!voiceName

  const handlePreview = useCallback(
    (key: string, url: string | null | undefined) => (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!url) return

      if (playingKey === key) {
        previewAudioRef.current?.pause()
        previewAudioRef.current = null
        setPlayingKey(null)
        return
      }

      previewAudioRef.current?.pause()
      const audio = new Audio(url)
      audio.onended = () => setPlayingKey(null)
      audio.play().catch(() => setPlayingKey(null))
      previewAudioRef.current = audio
      setPlayingKey(key)
    },
    [playingKey],
  )

  useEffect(() => {
    return () => previewAudioRef.current?.pause()
  }, [])

  return (
    <div
      className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto p-3"
      data-testid="voice-tab-content"
    >
      {!hasVoice ? (
        <div
          className="flex flex-col items-center justify-center gap-3 rounded-lg border border-border-subtle bg-surface-0 p-4 text-text-muted"
          data-testid="voice-tab-placeholder"
        >
          <svg className="w-8 h-8 text-text-muted/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
          </svg>
          <span className="text-sm font-medium">{t('voiceTabContent.voiceProfile')}</span>
          {onRegenerateVoice && hasCharacterSheet ? (
            <button
              onClick={(e) => { e.stopPropagation(); onRegenerateVoice() }}
              disabled={isRunning}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors nodrag',
                isRunning
                  ? 'bg-surface-1 text-text-muted cursor-not-allowed'
                  : 'bg-primary/10 text-primary hover:bg-primary/20'
              )}
              data-testid="voice-generate-button"
            >
              {isRunning ? t('voiceTabContent.voiceBeingGenerated') : t('voiceTabContent.generateVoice')}
            </button>
          ) : (
            <span className="text-xs font-medium text-text-muted/60">{t('voiceTabContent.comingSoon')}</span>
          )}
        </div>
      ) : (
      <div className="rounded-lg border border-input-border bg-input-bg p-3" data-testid="voice-current-card">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xxs uppercase tracking-wider text-text-muted">{t('voiceTabContent.currentCharacterVoice')}</p>
            <p className="mt-1 truncate text-sm font-semibold text-foreground">{voiceName}</p>
          </div>
          {onRegenerateVoice && hasCharacterSheet && (
            <button
              onClick={(e) => { e.stopPropagation(); onRegenerateVoice() }}
              disabled={isRunning}
              className={cn(
                'flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors nodrag focus:outline-none focus:ring-2 focus:ring-primary/20',
                isRunning
                  ? 'cursor-not-allowed border-input-border bg-surface-1 text-text-muted'
                  : 'border-input-border bg-surface-1 text-foreground hover:border-input-border-hover hover:bg-surface-3',
              )}
              data-testid="voice-regenerate-button"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isRunning && 'animate-spin')} strokeWidth={2} />
              {isRunning ? t('voiceTabContent.voiceBeingGenerated') : t('voiceTabContent.regenerateVoice')}
            </button>
          )}
        </div>

        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={handlePreview('current', voiceSampleUrl)}
            disabled={!voiceSampleUrl}
            className={cn(
              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20',
              voiceSampleUrl
                ? 'border-input-border bg-surface-1 text-foreground hover:border-input-border-hover hover:bg-surface-3'
                : 'cursor-not-allowed border-input-border bg-surface-1 text-text-muted',
            )}
            data-testid="voice-play-button"
            aria-label={playingKey === 'current' ? t('voiceTabContent.pauseCurrentVoice') : t('voiceTabContent.playCurrentVoice')}
          >
            {playingKey === 'current' ? (
              <Pause className="h-4 w-4" fill="currentColor" strokeWidth={2} />
            ) : (
              <Play className="ml-0.5 h-4 w-4" fill="currentColor" strokeWidth={2} />
            )}
          </button>

          <div className="flex h-8 items-center gap-1" data-testid="voice-waveform">
            {WAVEFORM_BAR_HEIGHTS.map((h, i) => (
              <div
                key={i}
                className={cn(
                  'w-1 rounded-full transition-all',
                  playingKey === 'current' ? 'bg-primary' : 'bg-text-muted/50',
                )}
                style={{ height: `${h * 3}px` }}
              />
            ))}
          </div>
        </div>

        {speechText && (
          <p
            className="mt-3 text-xs font-medium italic leading-relaxed text-text-muted"
            data-testid="voice-speech-text"
          >
            &ldquo;{speechText}&rdquo;
          </p>
        )}
      </div>
      )}

      {variants.length > 0 && (
        <div className="rounded-lg border border-border-subtle bg-surface-0 p-3" data-testid="voice-variants-section">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">{t('voiceTabContent.voiceVariants')}</p>
            <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
              {variants.length}
            </span>
          </div>
          <div className="mt-2 space-y-2">
            {[...variants].reverse().map((variant) => {
              const isSelected = variant.index === selectedVariantIndex
              const key = `variant-${variant.index}`
              return (
                <div
                  key={variant.index}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border p-2 transition-colors',
                    isSelected
                      ? 'border-primary/40 bg-primary/10'
                      : 'border-border-subtle bg-surface-1',
                  )}
                  data-testid={`voice-variant-${variant.index}`}
                >
                  <button
                    type="button"
                    onClick={handlePreview(key, variant.audioUrl)}
                    disabled={!variant.audioUrl}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-input-border bg-surface-0 text-text-muted transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground disabled:cursor-not-allowed disabled:hover:border-input-border disabled:hover:bg-surface-0 disabled:hover:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid={`voice-variant-play-${variant.index}`}
                    aria-label={playingKey === key ? t('voiceTabContent.pauseVoiceVariant', { number: variant.index + 1 }) : t('voiceTabContent.playVoiceVariant', { number: variant.index + 1 })}
                  >
                    {playingKey === key ? (
                      <Pause className="h-3.5 w-3.5" fill="currentColor" strokeWidth={2} />
                    ) : (
                      <Play className="ml-0.5 h-3.5 w-3.5" fill="currentColor" strokeWidth={2} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => onSelectVariant?.(variant.index)}
                    disabled={isSelected || !onSelectVariant}
                    className="min-w-0 flex-1 text-left focus:outline-none"
                    data-testid={`voice-variant-select-${variant.index}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-foreground">v{variant.index + 1}</span>
                      {isSelected && (
                        <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-xxs text-primary">
                          {t('voiceTabContent.current')}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs font-medium text-text-muted">
                      {variant.voiceName || variant.voiceId || t('voiceTabContent.unnamedVoice')}
                    </p>
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {voiceCatalog ? <VoiceCatalogPicker {...voiceCatalog} /> : null}
    </div>
  )
})

VoiceTabContent.displayName = 'VoiceTabContent'

export { VoiceTabContent }
